import { useCallback, useMemo } from "react";
import { useController, useFormContext, useFormState, useWatch } from "react-hook-form";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { MinusCircle } from "lucide-react";
import { TableCell, TableRow } from "../../../../../../../../../components/ui/table";
import CustomTimeInput from "../../../../../../../../../components/shared/inputs/CustomTimeInput";
import DurationInput, { getDurationInputValue } from "../../../../../../../../../components/shared/inputs/DurationInput";
import EditModal from "./EditModal";
import PayCodeSelect from "../../components/PayCodeSelect";
import LevelController from "./LevelController";
import { useGroups } from "../../../../../../../../Settings/General/OrganisationLevelGroups/context";
import { useScheduleContext } from "../../../../../../hooks/context/useScheduleContext";
import { getOrderedWeekdays } from "../../../../../../helpers/getOrderedWeekdays";
import { useRowGroupBy } from "../../../../../../hooks/form/useRowGroupBy";
import { combineDateAndTime, renderDurationAsFormat } from "../../../../../../../../../utils/helpers/date";
import { cn } from "../../../../../../../../../lib/utils";
import type { Resource } from "../../../../../../types/Resource";
import type { Weekday } from "../../../../../../../../../components/shared/inputs/WeekdaySelect/types";

type SectionProps = {
    index: number;
    remove: () => void;
    isFirstSection: boolean;
    isLastSection: boolean;
};

function Section({ index, remove, isFirstSection, isLastSection }: SectionProps) {
    const { t } = useTranslation();
    const { selectedCellOrEvent } = useScheduleContext();
    const { allocatedGroups } = useGroups();
    const rowGroupBy = useRowGroupBy();
    const { control, setValue, trigger } = useFormContext();
    const orderedWeekdays = getOrderedWeekdays();

    const watchedSections = useWatch({ control, name: "sections" });
    const sections = useMemo(() => watchedSections || [], [watchedSections]);

    const date = useMemo(() => {
        const isStrictDate = moment(selectedCellOrEvent?.date, "YYYY-MM-DD", true).isValid();
        const date = selectedCellOrEvent?.isTemplate
            ? (isStrictDate
                ? moment(selectedCellOrEvent?.date).format("YYYY-MM-DD")
                : moment().weekday(orderedWeekdays.indexOf(selectedCellOrEvent?.date as Weekday)).format("YYYY-MM-DD")
            ) : moment(selectedCellOrEvent?.date).format("YYYY-MM-DD");
        return date;
    }, [selectedCellOrEvent?.date, selectedCellOrEvent?.isTemplate, orderedWeekdays]);

    const startField = useController({
        control,
        name: `sections.${index}.start`,
        shouldUnregister: false,
    });

    const endField = useController({
        control,
        name: `sections.${index}.end`,
        shouldUnregister: false,
    });

    const payCodeField = useController({
        control,
        name: `sections.${index}.payCode`,
        shouldUnregister: false,
    });

    const durationField = useController({
        control,
        name: `sections.${index}.duration`,
        shouldUnregister: false,
    });

    const handleStartChange = useCallback((value: any) => {
        const endTime = sections[index]?.end;
        startField.field.onChange(value);
        if (value) {
            if (endTime) {
                const startDate = combineDateAndTime(moment(date), moment(value));
                let endDate = combineDateAndTime(moment(date), moment(endTime));

                if (moment(endDate).isBefore(moment(startDate), "seconds")) {
                    endDate = moment(endDate).add(1, "day");
                    setValue(`sections.${index}.end`, endDate);
                }

                const duration = moment(endDate).diff(moment(startDate), "seconds");
                if (duration > 0) {
                    setValue(`sections.${index}.duration`, renderDurationAsFormat(duration, "HH:mm"));
                }

                setValue(`sections.${index}.hasChanges`, {
                    time: moment().unix(),
                    field: "start",
                });
                if (isFirstSection) {
                    setValue("startDate", startDate);
                }
            }
        }
        trigger([`sections.${index}.start`, `sections.${index}.end`, `sections.${index}.duration`]);
    }, [sections, index, date, setValue, trigger, isFirstSection, startField.field]);

    const handleEndChange = useCallback((value: any) => {
        const startTime = sections[index]?.start;
        endField.field.onChange(value);
        if (value) {
            if (startTime) {
                const startDate = combineDateAndTime(moment(date), moment(startTime));
                let endDate = combineDateAndTime(moment(date), moment(value));
                if (moment(endDate).isBefore(moment(startDate), "seconds")) {
                    endDate = moment(endDate).add(1, "day");
                    setValue(`sections.${index}.end`, endDate);
                }

                const duration = moment(endDate).diff(moment(startDate), "seconds");
                if (duration > 0) {
                    setValue(`sections.${index}.duration`, renderDurationAsFormat(duration, "HH:mm"));
                }

                setValue(`sections.${index}.hasChanges`, {
                    time: moment().unix(),
                    field: "end",
                });
                if (isLastSection) {
                    setValue("endDate", endDate);
                }
            }
        }
        trigger([`sections.${index}.start`, `sections.${index}.end`, `sections.${index}.duration`]);
    }, [sections, index, date, setValue, trigger, isLastSection, endField.field]);

    const handleDurationChange = useCallback((value: any) => {
        const startTime = sections[index]?.start;
        const endTime = sections[index]?.end;
        durationField.field.onChange(value);
        if (!value) return;
        const duration = getDurationInputValue(value);
        const start = date && startTime ? combineDateAndTime(moment(date), moment(startTime)) : undefined;
        const end = date && endTime ? combineDateAndTime(moment(date), moment(endTime)) : undefined;

        if (duration > 0) {
            if (startTime) {
                let endDate = moment(start || startTime).add(duration, "seconds");
                endDate = combineDateAndTime(moment(date), moment(endDate));
                setValue(`sections.${index}.end`, endDate);
                setValue(`sections.${index}.hasChanges`, {
                    time: moment().unix(),
                    field: "end",
                });
            } else if (endTime) {
                let startDate = moment(end || endTime).subtract(duration, "seconds");
                startDate = combineDateAndTime(moment(date), moment(startDate));
                setValue(`sections.${index}.start`, startDate);
                setValue(`sections.${index}.hasChanges`, {
                    time: moment().unix(),
                    field: "start",
                });
            }
        }
        trigger([`sections.${index}.start`, `sections.${index}.end`, `sections.${index}.duration`]);
    }, [sections, index, date, setValue, trigger, durationField.field]);

    const lastBreakIndex = useMemo(() => {
        return sections?.map((section: any) => section.type).lastIndexOf("break")
    }, [sections]);

    const disabledWhenTypeBreak = useMemo(() => {
        const currentSection = sections?.[index];
        const calcGroupBreaks = (selectedCellOrEvent?.user as Resource)?.calculationGroup?.breaks;
        return !!(calcGroupBreaks?.automatic?.status &&
            currentSection?.type === "break" &&
            (sections?.[index - 1]?.type !== "break" ||
                sections?.[index + 1]?.type !== "break") &&
            sections?.[index - 1] &&
            sections?.[index + 1] &&
            (selectedCellOrEvent?.isCreating || selectedCellOrEvent?.addAnotherEvent));
    }, [sections, index, selectedCellOrEvent]);

    const disableStart = lastBreakIndex > -1 &&
        ((index < lastBreakIndex && index > 0) || index === lastBreakIndex + 1) &&
        (selectedCellOrEvent?.isCreating || selectedCellOrEvent?.addAnotherEvent);

    const disableEnd = lastBreakIndex > -1 && index < lastBreakIndex &&
        (selectedCellOrEvent?.isCreating || selectedCellOrEvent?.addAnotherEvent);

    const { errors } = useFormState({ control });
    const sectionErrors = (errors?.sections as any)?.[index] as Record<string, any> | undefined;

    const errorClassName = "tw-border-red-500 focus-visible:tw-border-red-500 focus-visible:tw-ring-red-500/20";
    const rowErrorMessage =
        sectionErrors?.start?.message ||
        sectionErrors?.end?.message ||
        sectionErrors?.payCode?.message ||
        sectionErrors?.duration?.message ||
        Object.values(sectionErrors || {}).find((error: any) => error?.message)?.message;
    const columnsCount = 4 + (allocatedGroups?.length || 0) + 1;

    return (
        <>
            <TableRow className={cn(rowErrorMessage && "tw-border-0")}>
                <TableCell className="tw-w-40">
                    <CustomTimeInput
                        placeholder={t("start")}
                        value={startField.field.value}
                        onChange={handleStartChange}
                        disabled={disabledWhenTypeBreak || disableStart}
                        className={cn(startField.fieldState.error?.message && errorClassName)}
                    />
                </TableCell>
                <TableCell className="tw-w-40">
                    <CustomTimeInput
                        placeholder={t("end")}
                        value={endField.field.value}
                        onChange={handleEndChange}
                        disabled={disabledWhenTypeBreak || disableEnd}
                        className={cn(endField.fieldState.error?.message && errorClassName)}
                    />
                </TableCell>
                <TableCell className="tw-w-44">
                    <PayCodeSelect
                        value={payCodeField.field.value}
                        onChange={payCodeField.field.onChange}
                        filterName="payCode"
                        disabled={disabledWhenTypeBreak || disableEnd}
                        className={cn(payCodeField.fieldState.error?.message && errorClassName)}
                    />
                </TableCell>
                <TableCell className="tw-w-40">
                    <DurationInput
                        placeholder={t("duration")}
                        value={durationField.field.value}
                        disabled={disabledWhenTypeBreak || disableEnd}
                        onChange={handleDurationChange}
                        className={cn(durationField.fieldState.error?.message && errorClassName)}
                    />
                </TableCell>

                {allocatedGroups?.map((group: any) => (
                    <TableCell key={group.id} className="tw-w-44">
                        <LevelController
                            control={control}
                            name={`sections.${index}.${group.level.replaceAll("_", "")}`}
                            group={group}
                            disabled={
                                disabledWhenTypeBreak ||
                                disableEnd ||
                                rowGroupBy?.field === group.level.replaceAll("_", "")
                            }
                        />
                    </TableCell>
                ))}

                <TableCell className="tw-w-20">
                    <div className="tw-flex tw-gap-2">
                        {sections?.length > 1 && (
                            <MinusCircle
                                className={cn(disabledWhenTypeBreak ? "tw-text-neutral-300" : "tw-text-rose-500 tw-cursor-pointer")}
                                size={16}
                                onClick={disabledWhenTypeBreak ? undefined : remove}
                            />
                        )}
                        <EditModal
                            index={index}
                            disabled={disabledWhenTypeBreak || disableEnd}
                        />
                    </div>
                </TableCell>
            </TableRow>

            {rowErrorMessage && (
                <TableRow>
                    <TableCell colSpan={columnsCount} className="tw-text-rose-500 tw-pb-3">
                        {rowErrorMessage}
                    </TableCell>
                </TableRow>
            )}
        </>
    );
}

export default Section;
